/**
 * Las cuentas que necesita un gráfico, y nada más.
 *
 * No hay librería de gráficos en este proyecto y no la va a haber por seis
 * formas: la aplicación tiene seis dependencias en total, tres de ellas
 * tipografías, y cualquier librería trae consigo la posibilidad de que un
 * componente escriba un color que no salga de un token — que es justo lo que
 * `.claude/rules/frontend.md` prohíbe. Un `<svg>` propio no puede.
 */

import { toNumber } from "../format";

/**
 * Lo que la API manda DE VERDAD para un importe: puede ser una cadena.
 *
 * Las rutas se anotan `-> dict[str, Any]`, y con esa anotación FastAPI
 * serializa cada `Decimal` como CADENA — `"1201353661.68"`, no
 * `1201353661.68`—. Tiparlo `number` no lo convierte en número: solo impide
 * que TypeScript avise de que hay que convertirlo.
 *
 * Esto costó cuatro gráficos en blanco sin un solo error en consola. Todos
 * comprueban `Number.isFinite` antes de dibujar —defensa correcta—, y una
 * cadena no lo es: el dominio caía a `[0, 1]`, el eje entero salía rotulado
 * «0.0 M» y no se trazaba ni una línea. El fallo era silencioso justo porque
 * la defensa funcionaba.
 *
 * Por eso el tipo es honesto y la conversión ocurre UNA vez, al entrar al
 * gráfico. Las tarjetas ya lo hacían con `toNumber`; los gráficos no.
 */
export type Cifra = number | string | null | undefined;

/** Un importe de la API como número, o `null` cuando no lo hay. */
export function cifra(valor: Cifra): number | null {
  return toNumber(valor) ?? null;
}

/** Lo mismo para una serie entera. Se aplica al ENTRAR, nunca por punto. */
export function cifras(valores: readonly Cifra[]): (number | null)[] {
  return valores.map(cifra);
}

/** Extremos de una serie, con el cero SIEMPRE dentro. */
export function extremos(valores: number[]): [number, number] {
  const limpios = valores.filter((v) => Number.isFinite(v));
  if (!limpios.length) return [0, 1];
  // El cero entra siempre. Una barra que arranca en el mínimo de la serie
  // exagera la diferencia entre dos cifras parecidas — es la forma más común de
  // mentir con un gráfico honesto.
  const min = Math.min(0, ...limpios);
  const max = Math.max(0, ...limpios);
  return min === max ? [min, min + 1] : [min, max];
}

/** Interpolación lineal de un dominio a un rango de píxeles. */
export function escala(
  [d0, d1]: [number, number],
  [r0, r1]: [number, number],
): (v: number) => number {
  const span = d1 - d0 || 1;
  return (v) => r0 + ((v - d0) / span) * (r1 - r0);
}

/**
 * Marcas del eje: `cuantas + 1` cortes redondeados que cubren el dominio.
 *
 * Redondeados a una potencia de diez por su magnitud, para que el eje diga
 * «60 M» y no «57.34 M»: un eje con decimales arbitrarios se lee como un dato.
 */
export function marcas([d0, d1]: [number, number], cuantas = 4): number[] {
  const paso = (d1 - d0) / cuantas;
  if (!Number.isFinite(paso) || paso <= 0) return [d0];
  const magnitud = 10 ** Math.floor(Math.log10(paso));
  const bonito = [1, 2, 2.5, 5, 10].find((m) => m * magnitud >= paso) ?? 10;
  const salto = bonito * magnitud;
  const inicio = Math.floor(d0 / salto) * salto;
  const out: number[] = [];
  for (let v = inicio; v <= d1 + salto / 1000; v += salto) out.push(v);
  return out;
}

/** El `d` de una polilínea. Los huecos parten la línea en vez de inventarla. */
export function ruta(
  puntos: (number | null)[],
  x: (i: number) => number,
  y: (v: number) => number,
): string {
  let d = "";
  let cortado = true;
  puntos.forEach((v, i) => {
    if (v === null || !Number.isFinite(v)) {
      cortado = true;
      return;
    }
    const px = x(i).toFixed(1);
    const py = y(v).toFixed(1);
    // Un mes con dato entre dos huecos emitía SOLO un `moveto`, y un trazo sin
    // segmento no dibuja nada: el valor desaparecía del gráfico. El segmento de
    // longitud cero lo hace visible como punto, porque el trazo va con
    // `stroke-linecap: round`.
    d += cortado ? `M${px} ${py}L${px} ${py}` : `L${px} ${py}`;
    cortado = false;
  });
  return d;
}
